import React from 'react';
import { Box, ButtonV2, Flex, TextV2, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { BANK_PARTNER_PROMO_FONT_FAMILY } from '@shared/components/TransferToBankDialog';

const ALLY_BRAND_COLOR = '#6b2d8b';

const StyledViewSlide = styled.div`
  @keyframes ally-promo-slide-in {
    from {
      opacity: 0;
      transform: translateX(${pxToRem(24)});
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  animation: ally-promo-slide-in 0.25s ease-out;
`;

const AllyLogoBox = styled(Box)`
  background: ${ALLY_BRAND_COLOR};
  border-radius: ${pxToRem(8)};
  padding: ${pxToRem(5)} ${pxToRem(11)};
  font-size: ${pxToRem(14)};
  font-weight: 800;
  font-family: ${BANK_PARTNER_PROMO_FONT_FAMILY};
  color: white;
  letter-spacing: -0.3px;
  display: inline-block;
`;

const PartnerText = styled(TextV2)`
  font-family: ${BANK_PARTNER_PROMO_FONT_FAMILY};
  font-size: ${pxToRem(12)};
  color: #666;
`;

const Headline = styled(TextV2)`
  font-size: ${pxToRem(24)};
  font-weight: 700;
  font-family: ${BANK_PARTNER_PROMO_FONT_FAMILY};
  color: #111;
  line-height: 1.2;
  letter-spacing: -0.5px;
  margin-bottom: ${pxToRem(10)};
`;

const Sub = styled(TextV2)`
  font-size: ${pxToRem(15)};
  font-family: ${BANK_PARTNER_PROMO_FONT_FAMILY};
  color: #555;
  line-height: 1.6;
`;

const PrimaryButton = styled(ButtonV2)`
  width: 100%;
  margin-bottom: ${pxToRem(8)};
`;

const SkipButton = styled(ButtonV2)`
  width: 100%;
  font-size: ${pxToRem(13)};
  font-family: ${BANK_PARTNER_PROMO_FONT_FAMILY};
  color: #888;
  margin-bottom: ${pxToRem(4)};
  &:hover {
    color: #555;
  }
`;

const FinePrint = styled(TextV2)`
  font-size: ${pxToRem(10.5)};
  font-family: ${BANK_PARTNER_PROMO_FONT_FAMILY};
  color: #aaa;
  line-height: 1.6;
  text-align: center;
`;

type AllyPromoStepProps = Readonly<{
  onAdvance: () => void;
  onSkip: () => void;
}>;

export const AllyPromoStep: React.FC<AllyPromoStepProps> = ({ onAdvance, onSkip }) => {
  const { tq } = useTranslation('allyPartnershipAdmin');

  return (
    <StyledViewSlide>
      <Flex
        flexDirection="column"
        justifyContent="space-between"
        padding={6}
        gap={6}
        minHeight="380px"
      >
        <Flex
          flexDirection="column"
          gap={4}
        >
          <Flex
            alignItems="center"
            gap={3}
          >
            <AllyLogoBox>ally</AllyLogoBox>
            <PartnerText typographyVariant="label3">{tq`Partnered with Joy`}</PartnerText>
          </Flex>
          <Headline>{tq`Your new life starts with a little extra.`}</Headline>
          <Sub>{tq`Open an Ally savings account with Joy and get $200 on us — no fees, no minimums.`}</Sub>
        </Flex>
        <Flex flexDirection="column">
          <PrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            backgroundColor={ALLY_BRAND_COLOR}
            color="white"
            onClick={onAdvance}
          >
            {tq`Get $200 towards your new life`}
          </PrimaryButton>
          <SkipButton
            type="button"
            variant="ghost"
            intent="neutral"
            onClick={onSkip}
          >
            {tq`Maybe later`}
          </SkipButton>
          <FinePrint>{tq`$200 bonus offer subject to Ally Bank terms and conditions. Must open a new qualifying savings account.`}</FinePrint>
        </Flex>
      </Flex>
    </StyledViewSlide>
  );
};
